<template>
  <div class="flex flex-col gap-12 md:flex-row md:items-center">
    <RevInputText
      :id="`stock-input-${listing.id}`"
      v-model="localStock"
      data-qa="stock"
      :disabled="isSaving"
      :label="i18n(translations.inputLabel)"
      type="number"
      @keyup.enter="handleSave"
    />

    <RevButton
      v-if="isEditing && !isSaved"
      :disabled="!localStock"
      :loading="isSaving"
      size="small"
      variant="secondary"
      @click="handleSave"
    >
      {{ i18n(translations.saveButton) }}</RevButton
    >
  </div>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevInputText } from '@ds/components/InputText'

import type { ListingV2 } from '~/api/listings/types'
import { useListings } from '~/scopes/listings/pages/listingsV2/composables/useListings'

import translations from './Inventory.translations'

const props = defineProps<{
  listing: ListingV2
}>()

const { openErrorToast } = useTheToast()
const { updateListing } = useListings()
const isSaving = ref(false)
const i18n = useI18n()

const localStock = ref(props.listing.quantity)
const isSaved = ref(false)

const isEditing = computed(() => {
  return props.listing.quantity !== localStock.value
})

const canSave = computed(() => isEditing.value && localStock.value)

watch(localStock, () => {
  if (isSaved.value) {
    isSaved.value = false
  }
})

async function handleSave() {
  if (!canSave.value) return

  try {
    isSaving.value = true

    await updateListing(props.listing.id, {
      quantity: localStock.value,
    })
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  } finally {
    isSaving.value = false
    isSaved.value = true
  }
}
</script>
